import { useQuery } from 'react-query';
import type { UseQueryResult } from 'react-query';
import { Decimal } from 'decimal.js';
import type { AsyncReturnType } from 'type-fest';

import { subscriptionAPI } from '@hotjar/webapp/billing/subscription';
import type { Site } from '@hotjar/site';
import type { BillingPeriod } from '@hotjar/subscription-management';
import { mapV3BillingPeriodToV2BillingCycle } from '@hotjar/upsell';

interface Props {
  site: Site | null | undefined;
  planId: string | undefined;
  billingPeriod?: BillingPeriod;
  enabled?: boolean;
}

export interface GetCostResult {
  withoutDiscounts: Decimal;
  withDiscounts: Decimal;
}

const USE_PRODUCT_COST_QUERY_KEY = 'use_product_cost_query';
const ANNUAL_DEDUCTION_ID = 'ANNUAL_DEDUCTION';

/**
 * Uses the subscription API's preview action to retrieve pricing data about a site.
 *
 * This only works for account owners.
 */
const getCost = async (
  site: Site,
  planId: string,
  billingPeriod: BillingPeriod = 'MONTHLY',
): Promise<GetCostResult> => {
  const v2BillingCycle = mapV3BillingPeriodToV2BillingCycle(billingPeriod);

  const { future: priceSummary } = await subscriptionAPI.preview(
    site.account,
    [
      {
        site_id: site.id,
        plan_id: planId,
        always_on_rate: 0,
      },
    ],
    v2BillingCycle,
    undefined,
  );

  const sitePrice = priceSummary.lineItems.find(
    (lineItem) => lineItem.sitePlanInfo.siteReference.siteId === site.id,
  );

  /**
   * If billing period is annually, we want to always use the subtotal price (price without any discounts applied) and only apply
   * the ANNUAL_DEDUCTION deduction.
   * The reason of this is because the products cost don't have the correct deductions appled since they're only applied in the product level.
   * Nevertheless, to show some discount being applied when billing period is annually, we calculate here the annual discount retrieved from the subscription API
   */
  if (billingPeriod === 'ANNUALLY') {
    const annualDeduction = priceSummary.deductions.find(
      (deduction) => deduction.id === ANNUAL_DEDUCTION_ID,
    );
    const annualDeductionPercentage = annualDeduction
      ? parseInt(annualDeduction.percentage, 10)
      : 0;

    return {
      withoutDiscounts: sitePrice?.subtotal || new Decimal(0),
      withDiscounts: sitePrice
        ? sitePrice.subtotal.times(1 - annualDeductionPercentage / 100)
        : new Decimal(0),
    };
  }

  /**
   * Differently from the calclulation with annual billing, here we can set `withDiscounts` property with the site price `total`
   * as it will be the same as the subtotal (discounts are not applied)
   */
  return {
    withoutDiscounts: sitePrice?.subtotal || new Decimal(0),
    withDiscounts: sitePrice?.total || new Decimal(0),
  };
};

/**
 * Hook that wraps `getCost` into a `useQuery`. We cache the response per combination of site ID + plan ID + billing
 * period, so that consequent calls with the same input will not result in a network request.
 *
 * TODO: Rename this to better reflect what it does and how. Also, it should not mention product, as it's not
 * operating around products.
 *
 * TODO: we should find a way to consolidate `useProductPricing` and `useProductCost`
 */
export function useProductCost({
  site,
  planId,
  billingPeriod = 'MONTHLY',
  enabled: customEnabled = true,
}: Props): UseQueryResult<AsyncReturnType<typeof getCost>> {
  const enabled = !!site && !!planId && !!customEnabled;

  return useQuery({
    queryKey: [USE_PRODUCT_COST_QUERY_KEY, site?.id, planId, billingPeriod],
    queryFn: () => {
      if (!enabled) {
        throw new Error('useProductCost: Missing or empty input data.');
      }

      return getCost(site, planId, billingPeriod);
    },
    enabled,
    cacheTime: 60 * 60 * 1000, // 1 hour
    staleTime: 60 * 60 * 1000, // 1 hour
    refetchOnWindowFocus: false,
  });
}
